import { useEffect, type ReactNode } from 'react'
import { ArrowUp, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useChatStore } from '@/stores/chat-store'
import { useVoiceStore } from '@/stores/voice-store'
import { CompanionPlusMenu } from './companion-plus-menu'
import { CompanionEffortChip } from './companion-effort-chip'
import {
  CompanionCursorToggle,
  CompanionSpeakSend,
  CompanionVoiceSessionRow,
} from './companion-speak-send'
import { useChatTextarea } from './use-chat-textarea'

export type CompanionComposerVariant = 'drawer' | 'floating' | 'popout'

interface CompanionComposerProps {
  onSend: (text: string) => void
  disabled?: boolean
  placeholder?: string
  /** See useChatTextarea — one-shot pre-fill from "Ask AI about X". */
  seedText?: string | null
  onSeedConsumed?: () => void
  /** Mirrors every value change out (quick-action filtering). */
  onValueChange?: (value: string) => void
  /**
   * - `drawer`:  full controls row, own horizontal padding.
   * - `floating`: full controls row, flush — the window bar owns spacing.
   * - `popout`:  textarea + send only. The popout is a separate JS context
   *   (stores/queries don't sync over the BroadcastChannel), so every
   *   store-backed control is hidden there by design.
   */
  variant: CompanionComposerVariant
  /** Extra control at the end of the controls row (the floating pin). */
  trailing?: ReactNode
  /** Control inline at the START of the input row (the floating drag grip). */
  leading?: ReactNode
}

/**
 * The unified composer every companion surface mounts: one rounded glass
 * container holding the mention chips, the textarea, and the controls row
 * (`+` menu and effort chip left; send — later mic + Speak — right).
 */
export function CompanionComposer({
  onSend,
  disabled,
  placeholder = 'Message...',
  seedText,
  onSeedConsumed,
  onValueChange,
  variant,
  trailing,
  leading,
}: CompanionComposerProps) {
  const mentions = useChatStore((s) => s.mentions)
  const removeMention = useChatStore((s) => s.removeMention)
  // Voice is main-window only: the popout is a separate JS context with no
  // manager, so it keeps the plain send button.
  const showControls = variant !== 'popout'
  const voiceStatus = useVoiceStore((s) => s.status)
  const interimText = useVoiceStore((s) => s.interimText)
  const inVoiceSession = showControls && voiceStatus === 'voice-session'
  const dictating = showControls && voiceStatus === 'dictating'

  const {
    value,
    displayValue,
    inputRef,
    handleChange,
    handleKeyDown,
    handleSubmit,
    canSend,
    appendText,
    insertText,
  } = useChatTextarea({
    onSend,
    disabled,
    seedText,
    onSeedConsumed,
    onValueChange,
    // Dictation types straight into the box, live.
    overlayText: dictating && interimText ? interimText : undefined,
  })

  // A ticker dropped on the chat surface (see features/ticker-drag) arrives
  // through the store; insert it at the cursor and clear it.
  const composerInsert = useChatStore((s) => s.composerInsert)
  useEffect(() => {
    if (composerInsert == null) return
    insertText(composerInsert)
    useChatStore.getState().clearComposerInsert()
  }, [composerInsert, insertText])

  // The Mod+K shortcut asks for the cursor in the box (see use-ai-chat-shortcut).
  // preventScroll: the drawer is still animating its width from 0.
  const composerFocusPending = useChatStore((s) => s.composerFocusPending)
  useEffect(() => {
    if (!composerFocusPending || variant === 'popout') return
    const el = inputRef.current
    if (el) {
      el.focus({ preventScroll: true })
      el.setSelectionRange(el.value.length, el.value.length)
    }
    useChatStore.getState().clearComposerFocus()
  }, [composerFocusPending, variant, inputRef])

  return (
    <div
      className={cn(
        // --composer-ring drives the composer box's border, so the purple
        // focus highlight follows focus anywhere inside the composer.
        '[--composer-ring:hsl(var(--border))] focus-within:[--composer-ring:hsl(var(--primary)/0.4)]',
        variant === 'drawer' && 'px-3 pb-1.5 pt-1',
        variant === 'popout' && 'px-3 py-2.5',
        variant === 'floating' && 'w-full',
      )}
    >
      <div
        className={cn(
          // The floating window is already a bordered box — no box-in-a-box
          // (Chris, 2026-08-28), and it runs HALF the inset of the docked
          // surfaces to stay featherweight (Chris, 2026-08-29).
          // rounded-lg matches the app's panel radius (--radius, 8px) — the
          // 2xl of the reference design read too soft next to real panels.
          variant === 'floating'
            ? 'flex flex-col gap-0.5 px-1 pb-1 pt-1'
            : 'flex flex-col gap-1 rounded-lg border border-[color:var(--composer-ring,hsl(var(--border)))] bg-muted/20 px-2 pb-1.5 pt-2 shadow-sm backdrop-blur-sm transition-colors',
        )}
      >
        {mentions.length > 0 && (
          <div className="flex flex-wrap gap-1.5 px-1">
            {mentions.map((m) => (
              <span
                key={m.panelId}
                className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
              >
                {m.title}
                <button
                  type="button"
                  aria-label={`Remove ${m.title} mention`}
                  onClick={() => removeMention(m.panelId)}
                  className="rounded-full text-primary/70 hover:text-primary"
                >
                  <X size={11} />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="flex items-center gap-0.5">
          {leading}
          <textarea
            ref={inputRef}
            value={displayValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            // While dictating the box is a live transcript — typing would
            // fight the overlay; the mic button (or silence) hands it back.
            readOnly={dictating}
            rows={1}
            className={cn(
              'w-full flex-1 resize-none border-0 bg-transparent px-1.5 py-0.5 outline-hidden',
              // Floating: typed text matches the chip size (effort/mode) so
              // the whole bar reads as one compact unit.
              variant === 'floating' ? 'text-xs' : 'text-sm',
              // Floating opens two lines tall so it reads as a place to type
              // (Chris, 2026-10-02); auto-resize still grows it from there.
              variant === 'floating' && 'min-h-11',
              'placeholder:text-muted-foreground/50 focus:ring-0',
              disabled && 'cursor-not-allowed opacity-50',
            )}
          />
        </div>

        {/* Live transcript for the voice SESSION (dictation types straight
            into the textarea instead — see overlayText above). */}
        {inVoiceSession && interimText && (
          <p
            data-testid="live-transcript"
            className="px-1.5 text-xs italic text-muted-foreground/70"
          >
            {interimText}
          </p>
        )}

        {inVoiceSession ? (
          <CompanionVoiceSessionRow />
        ) : (
          <div className="flex items-center gap-1">
            {showControls && <CompanionPlusMenu />}
            {showControls && <CompanionEffortChip />}
            <div className="flex-1" />
            {trailing}
            {showControls && <CompanionCursorToggle />}
            {showControls ? (
              <CompanionSpeakSend
                hasText={value.trim() !== ''}
                canSend={canSend}
                onSubmit={handleSubmit}
                onDictationFinal={appendText}
              />
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSend}
                aria-label="Send message"
                className={cn(
                  'flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
                  'bg-primary text-primary-foreground transition-colors hover:bg-primary/90',
                  'disabled:cursor-not-allowed disabled:opacity-40',
                )}
              >
                <ArrowUp size={14} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
